import { Box, useDisclosure, type MenuItemProps } from '@chakra-ui/react';
import {
  useEffect,
  type FC,
  type JSX,
  type ReactNode,
  type RefObject,
} from 'react';

import { GenericFilterSortDropdown } from '$/components/common/GenericFilterSortDropdown';
import { FilterSkeleton } from '$/components/core/Collection/CollectionFilter/FilterSkeleton';
import { useFilterOptions } from '$/hooks/data/useFilterOptions';

interface Props extends Omit<MenuItemProps, 'type' | 'children'> {
  title: string;
  children: (onMenuClose: () => void) => ReactNode;
  loadingComponent?: JSX.Element;
  scrollRefs?: RefObject<HTMLDivElement | null>[];
  dataTestId?: string;
}

export const FilterDropdown: FC<Props> = ({
  title,
  loadingComponent,
  children,
  scrollRefs = [],
  dataTestId,
  ...props
}) => {
  const { isOpen, onClose, onOpen } = useDisclosure();

  const { data: filter } = useFilterOptions();

  useEffect(() => {
    const handleScroll = () => isOpen && onClose();

    const cleanup = scrollRefs.map((ref) => {
      const current = ref.current;
      current?.addEventListener('scroll', handleScroll, { passive: true });
      return () => current?.removeEventListener('scroll', handleScroll);
    });

    return () => cleanup.forEach((fn) => fn());
  }, [scrollRefs, isOpen, onClose]);

  if (!filter) {
    return loadingComponent ?? <FilterSkeleton title={title} />;
  }

  return (
    <Box>
      <GenericFilterSortDropdown
        title={title}
        closeOnSelect={false}
        isOpen={isOpen}
        onClose={onClose}
        onOpen={onOpen}
        dataTestId={dataTestId}
        {...props}
      >
        {children(onClose)}
      </GenericFilterSortDropdown>
    </Box>
  );
};
